import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  BatchDeleteFeesRequest,
  BatchDeleteFeesResponse,
  BatchUpdateIncludeRequest,
  BatchUpdateIncludeResponse,
  CreateFeeRequest,
  CreateFeeResponse,
  DeleteFeeRequest,
  DeleteFeeResponse,
  FeeManagerFetchInterface,
  FeeManagerFetchWriteInterface,
  FeeType,
  GetFeeManagementStatusResponse,
  GetFeesResponse,
  ToggleFeeManagementRequest,
  ToggleFeeManagementResponse,
  UpdateFeeRequest,
  UpdateFeeResponse
} from "@dealer-center/ddc-pages-fee-manager-interface";

const queryKeyBase = "ddc-pages-fee-manager";

// GET hooks

export const useFees = ({
  accountId,
  type,
  disabled
}: FeeManagerFetchInterface & { type: FeeType }) => {
  return useQuery({
    queryKey: [queryKeyBase, "fees", accountId, type],
    queryFn: () => getFees(accountId, type),
    enabled: !disabled && !!accountId && !!type,
    retry: 1,
    staleTime: 1000 * 60 // 1 minute
  });
};

export const useVersion = () => {
  return useQuery({
    queryKey: [queryKeyBase, "version"],
    queryFn: getVersion,
    retry: 1,
    staleTime: Infinity
  });
};

export const useFeeManagementStatus = ({
  accountId,
  disabled
}: FeeManagerFetchInterface) => {
  return useQuery({
    queryKey: [queryKeyBase, "status", accountId],
    queryFn: () => getFeeManagementStatus(accountId),
    enabled: !disabled && !!accountId,
    retry: 1,
    staleTime: 0 // Always refetch when invalidated
  });
};

// Mutation hooks

export const useCreateFee = ({
  accountId,
  afterSuccess
}: FeeManagerFetchWriteInterface) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: Omit<CreateFeeRequest, "accountId">) =>
      createFee({ ...request, accountId }),
    onSuccess: data => {
      // Invalidate and refetch fees for this type
      queryClient.invalidateQueries({
        queryKey: [queryKeyBase, "fees", accountId, data.fee.type]
      });

      if (typeof afterSuccess === "function") {
        afterSuccess(data);
      } else if (typeof afterSuccess === "string") {
        queryClient.invalidateQueries({
          queryKey: [queryKeyBase, afterSuccess]
        });
      }
    },
    onError: error => {
      console.error("[useCreateFee] mutation failed:", error);
    }
  });
};

export const useUpdateFee = ({
  accountId,
  afterSuccess
}: FeeManagerFetchWriteInterface) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      type,
      ...request
    }: Omit<UpdateFeeRequest, "accountId"> & { type: FeeType }) =>
      updateFee({ ...request, accountId }, type),
    onSuccess: data => {
      // Invalidate and refetch fees for this type
      queryClient.invalidateQueries({
        queryKey: [queryKeyBase, "fees", accountId, data.fee.type]
      });

      if (typeof afterSuccess === "function") {
        afterSuccess(data);
      } else if (typeof afterSuccess === "string") {
        queryClient.invalidateQueries({
          queryKey: [queryKeyBase, afterSuccess]
        });
      }
    },
    onError: error => {
      console.error("[useUpdateFee] mutation failed:", error);
    }
  });
};

export const useDeleteFee = ({
  accountId,
  afterSuccess
}: FeeManagerFetchWriteInterface) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      type,
      ...request
    }: Omit<DeleteFeeRequest, "accountId"> & { type: FeeType }) =>
      deleteFee({ ...request, accountId }, type),
    onSuccess: (_, variables) => {
      // Invalidate and refetch fees for this type
      queryClient.invalidateQueries({
        queryKey: [queryKeyBase, "fees", accountId, variables.type]
      });

      if (typeof afterSuccess === "function") {
        afterSuccess(undefined);
      } else if (typeof afterSuccess === "string") {
        queryClient.invalidateQueries({
          queryKey: [queryKeyBase, afterSuccess]
        });
      }
    },
    onError: error => {
      console.error("[useDeleteFee] mutation failed:", error);
    }
  });
};

export const useBatchUpdateInclude = ({
  accountId,
  afterSuccess
}: FeeManagerFetchWriteInterface) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: Omit<BatchUpdateIncludeRequest, "accountId">) =>
      batchUpdateInclude({ ...request, accountId }),
    onSuccess: (_, variables) => {
      // Invalidate and refetch fees for this type
      queryClient.invalidateQueries({
        queryKey: [queryKeyBase, "fees", accountId, variables.type]
      });

      if (typeof afterSuccess === "function") {
        afterSuccess(undefined);
      } else if (typeof afterSuccess === "string") {
        queryClient.invalidateQueries({
          queryKey: [queryKeyBase, afterSuccess]
        });
      }
    },
    onError: error => {
      console.error("[useBatchUpdateInclude] mutation failed:", error);
    }
  });
};

export const useBatchDeleteFees = ({
  accountId,
  afterSuccess
}: FeeManagerFetchWriteInterface) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: Omit<BatchDeleteFeesRequest, "accountId">) =>
      batchDeleteFees({ ...request, accountId }),
    onSuccess: (_, variables) => {
      // Invalidate and refetch fees for this type
      queryClient.invalidateQueries({
        queryKey: [queryKeyBase, "fees", accountId, variables.type]
      });

      if (typeof afterSuccess === "function") {
        afterSuccess(undefined);
      } else if (typeof afterSuccess === "string") {
        queryClient.invalidateQueries({
          queryKey: [queryKeyBase, afterSuccess]
        });
      }
    },
    onError: error => {
      console.error("[useBatchDeleteFees] mutation failed:", error);
    }
  });
};

export const useToggleFeeManagement = ({
  accountId,
  afterSuccess
}: FeeManagerFetchWriteInterface) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: Omit<ToggleFeeManagementRequest, "accountId">) =>
      toggleFeeManagement({ ...request, accountId }),
    onSuccess: async () => {
      // Wait a moment for backend to settle, then refetch
      await new Promise(resolve => setTimeout(resolve, 100));

      // Force refetch status query to update UI immediately
      await queryClient.refetchQueries({
        queryKey: [queryKeyBase, "status", accountId]
      });

      if (typeof afterSuccess === "function") {
        afterSuccess(undefined);
      } else if (typeof afterSuccess === "string") {
        queryClient.invalidateQueries({
          queryKey: [queryKeyBase, afterSuccess]
        });
      }
    },
    onError: error => {
      console.error("[useToggleFeeManagement] mutation failed:", error);
    }
  });
};

// API functions

const getVersion = async (): Promise<{ version: string }> => {
  const res = await fetch(`/landing/nxlambda/feeManager/version`);
  if (res.ok) {
    return res.json();
  } else {
    throw new Error(res.statusText);
  }
};

const getFees = async (
  accountId: string,
  type: FeeType
): Promise<GetFeesResponse> => {
  const res = await fetch(
    `/landing/nxlambda/feeManager/fees/${type}?accountId=${accountId}`
  );
  if (res.ok) {
    return res.json();
  } else {
    throw new Error(res.statusText);
  }
};

const getFeeManagementStatus = async (
  accountId: string
): Promise<GetFeeManagementStatusResponse> => {
  const res = await fetch(
    `/landing/nxlambda/feeManager/management/status?accountId=${accountId}`
  );
  if (res.ok) {
    return res.json();
  } else {
    throw new Error(res.statusText);
  }
};

const createFee = async (
  request: CreateFeeRequest
): Promise<CreateFeeResponse> => {
  const res = await fetch(`/landing/nxlambda/feeManager/fees`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(request)
  });
  if (res.ok) {
    return res.json();
  } else {
    throw new Error(res.statusText);
  }
};

const updateFee = async (
  request: UpdateFeeRequest,
  type: FeeType
): Promise<UpdateFeeResponse> => {
  const res = await fetch(
    `/landing/nxlambda/feeManager/fees/${request.feeId}`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...request, type })
    }
  );
  if (res.ok) {
    return res.json();
  } else {
    throw new Error(res.statusText);
  }
};

const deleteFee = async (
  request: DeleteFeeRequest,
  type: FeeType
): Promise<DeleteFeeResponse> => {
  const res = await fetch(
    `/landing/nxlambda/feeManager/fees/${request.feeId}?accountId=${request.accountId}&type=${type}`,
    {
      method: "DELETE"
    }
  );
  if (res.ok) {
    return res.json();
  } else {
    throw new Error(res.statusText);
  }
};

const batchUpdateInclude = async (
  request: BatchUpdateIncludeRequest
): Promise<BatchUpdateIncludeResponse> => {
  const res = await fetch(
    `/landing/nxlambda/feeManager/fees/batch-update-include`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(request)
    }
  );
  if (res.ok) {
    return res.json();
  } else {
    throw new Error(res.statusText);
  }
};

const batchDeleteFees = async (
  request: BatchDeleteFeesRequest
): Promise<BatchDeleteFeesResponse> => {
  const res = await fetch(`/landing/nxlambda/feeManager/fees/batch-delete`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(request)
  });
  if (res.ok) {
    return res.json();
  } else {
    throw new Error(res.statusText);
  }
};

const toggleFeeManagement = async (
  request: ToggleFeeManagementRequest
): Promise<ToggleFeeManagementResponse> => {
  const res = await fetch(`/landing/nxlambda/feeManager/management/toggle`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(request)
  });
  if (res.ok) {
    return res.json();
  } else {
    throw new Error(res.statusText);
  }
};

const resetMockData = async (): Promise<{
  success: boolean;
  message: string;
}> => {
  const res = await fetch(
    `/landing/nxlambda/feeManager/debug/reset-mock-data`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" }
    }
  );
  if (res.ok) {
    return res.json();
  } else {
    throw new Error(res.statusText);
  }
};

export const useResetMockData = ({
  accountId,
  afterSuccess
}: FeeManagerFetchWriteInterface) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => resetMockData(),
    onSuccess: async () => {
      // Invalidate all queries to force refetch
      await queryClient.invalidateQueries({
        queryKey: [queryKeyBase]
      });

      if (typeof afterSuccess === "function") {
        afterSuccess(undefined);
      } else if (typeof afterSuccess === "string") {
        queryClient.invalidateQueries({
          queryKey: [queryKeyBase, afterSuccess]
        });
      }
    }
  });
};
